import { Injectable, inject } from '@angular/core';
import { from, map, Observable } from 'rxjs';
import {
  CatalogApi,
  CatalogContractOffer,
  CatalogEntry,
  PolicyConstraintAttributeKeyEnum,
  PolicyConstraint,
  PolicyConstraintOperator,
  PolicyRule,
  PolicyRuleType,
  PolicyValue,
  PolicyValueType
} from '@adesso/dataspace-api-client';
import { ADESSO_API_CONFIG } from '../../../../core/api/adesso-api.config';
import type { Catalog, Dataset } from '../models/catalog.model';
import type { Constraint, Policy } from '@htx/policies';
import { PolicyType } from '@htx/policies';
import { CatalogApiPort } from '../ports/catalog-api.port';

@Injectable()
export class AdessoCatalogApiAdapter extends CatalogApiPort {
  private readonly api = new CatalogApi(inject(ADESSO_API_CONFIG));

  getCatalogByConnectorAddress(url: string, cpId: string): Observable<Catalog> {
    return from(
      this.api.queryCatalog({ connectorEndpoint: url, participantId: cpId })
    ).pipe(map((res) => toCatalog(res.data ?? [], cpId)));
  }
}

function toCatalog(entries: CatalogEntry[], cpId: string): Catalog {
  return {
    id: cpId,
    datasets: entries.map(toDataset)
  };
}

function toDataset(entry: CatalogEntry): Dataset {
  const props = (entry.properties ?? {}) as Record<string, unknown>;
  return {
    id: entry.assetId ?? '',
    policies: (entry.contractOffers ?? []).map(toPolicy),
    distribution: [],
    name: str(props['name']),
    description: optionalStr(props['description']),
    contenttype: str(props['contenttype']),
    serviceType: str(props['serviceType']),
    serviceOfferName: optionalStr(props['serviceOfferName']),
    language: str(props['language']),
    creationDate: parseDate(props['creationDate']),
    lastModified: parseOptionalDate(props['lastModified']),
    publisher: str(props['publisher']),
    sovereign: str(props['sovereign']),
    dataOfferSource: optionalStr(props['dataOfferSource']),
    datasource: str(props['datasource']),
    method: str(props['method']),
    baseUrl: str(props['baseUrl']),
    keywords: normalizeKeywords(props['keywords']),
    subCategory: str(props['subCategory']),
    type: optionalStr(props['type'])
  };
}

function toPolicy(offer: CatalogContractOffer): Policy {
  return {
    id: offer.offerId ?? '',
    createdAt: 0,
    constraints: offer.policy ? flattenPolicyRule(offer.policy) : [],
    prohibition: [],
    obligation: [],
    privateProperties: { name: '', description: '' }
  };
}

function flattenPolicyRule(rule: PolicyRule): Constraint[] {
  switch (rule.ruleType) {
    case PolicyRuleType.CONSTRAINT:
      return rule.constraint ? [mapTypedConstraint(rule.constraint)] : [];
    case PolicyRuleType.AND:
    case PolicyRuleType.OR:
    case PolicyRuleType.XONE:
      return (rule.operands ?? []).flatMap(flattenPolicyRule);
    case PolicyRuleType.EMPTY:
    default:
      return [];
  }
}

function mapTypedConstraint(c: PolicyConstraint): Constraint {
  const value = c.value;

  switch (c.attributeKey) {
    case PolicyConstraintAttributeKeyEnum.BUSINESS_PARTNER_NUMBER:
      return {
        type: PolicyType.PARTICIPANT_RESTRICTED,
        bpnNumbers: extractBpnList(value, c.operator)
      };

    case PolicyConstraintAttributeKeyEnum.DATA_USAGE_END_DURATION_DAYS:
      return {
        type: PolicyType.TIME_PERIOD_RESTRICTED,
        count: Number(extractText(value)) || 0
      };

    case PolicyConstraintAttributeKeyEnum.MEMBERSHIP:
      return { type: PolicyType.MEMBERSHIP_STATIC };

    case PolicyConstraintAttributeKeyEnum.FRAMEWORK_AGREEMENT:
      return { type: PolicyType.FRAMEWORK_AGREEMENT };

    case PolicyConstraintAttributeKeyEnum.USAGE_PURPOSE:
      return {
        type: PolicyType.USE_CASE_MEMBERSHIP,
        useCaseId: extractText(value) as 'UC.digitalization.sc' | 'UC.shortage.sc' | 'UC.pcf'
      };

    case PolicyConstraintAttributeKeyEnum.DATA_USAGE_END_DATE:
      return {
        type: PolicyType.END_DATE,
        endDate: new Date(extractText(value))
      };

    case PolicyConstraintAttributeKeyEnum.AFFILIATES_REGION:
      return {
        type: PolicyType.COMPANY_REGION_RESTRICTED,
        region: extractText(value) as 'Europe'
      };

    case PolicyConstraintAttributeKeyEnum.JURISDICTION_LOCATION:
      return {
        type: PolicyType.DATACENTER_REGION_RESTRICTED,
        region: extractText(value) as 'Europe'
      };

    case PolicyConstraintAttributeKeyEnum.UNKNOWN:
    default:
      return {
        type: PolicyType.UNKNOWN,
        leftOperand: c.attributeUri ?? '',
        rightOperand: extractText(value)
      };
  }
}

function extractText(value: PolicyValue | undefined): string {
  if (!value) return '';
  if (value.valueType === PolicyValueType.TEXT_LIST) {
    return (value.textList ?? []).join(',');
  }
  return value.text ?? '';
}

function extractBpnList(value: PolicyValue | undefined, operator: PolicyConstraintOperator): string[] {
  if (!value) return [];
  if (value.valueType === PolicyValueType.TEXT_LIST) {
    return (value.textList ?? []).map((s) => s.trim()).filter(Boolean);
  }
  const raw = value.text ?? '';
  if (operator === PolicyConstraintOperator.IS_ANY_OF || operator === PolicyConstraintOperator.IN) {
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return raw ? [raw] : [];
}

function normalizeKeywords(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((k: unknown) => String(k).trim()).filter(Boolean);
  }
  if (typeof raw === 'string' && raw.trim()) {
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

function parseDate(v: unknown): Date {
  if (v == null) return new Date(0);
  const d = new Date(String(v));
  return isNaN(d.getTime()) ? new Date(0) : d;
}

function parseOptionalDate(v: unknown): Date | undefined {
  if (v == null) return undefined;
  const d = new Date(String(v));
  return isNaN(d.getTime()) ? undefined : d;
}

function str(v: unknown): string {
  return v == null ? '' : String(v);
}

function optionalStr(v: unknown): string | undefined {
  return v == null ? undefined : String(v);
}
